import { createSelector } from '@reduxjs/toolkit';

import {
  DynamicFlowStep,
  StepDocumentConfig,
  StepDocumentStyles,
  StepGovernmentIdStyles,
  StepSelfieReflection,
  StepSelfieStyles,
  UiStepType,
} from 'lib/interfaces/StepsConfig';
import { ButtonVerifyWithPersonaComponent, UiComponentType } from 'lib/interfaces/ui-components';
import { DocumentGeneric } from 'verify/models/document';
import { Inquiry } from 'verify/models/inquiry';
import { UploadedFile } from 'verify/redux/reducers/document';
import { InquiryState } from 'verify/redux/reducers/inquiry';
import { RootState } from 'verify/redux/store';

export const getDocumentStepConfig = (state: RootState): StepDocumentConfig | null => {
  const nextStep = state.inquiry.inquiry.attributes['next-step'];
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  return nextStep.type === UiStepType.Document ? nextStep.config : undefined;
};

export const getDocumentStepStyles = createSelector(
  [(inquiryState: InquiryState) => inquiryState.inquiry],
  (inquiry): StepDocumentStyles | null => {
    const nextStep = inquiry.attributes['next-step'];

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return nextStep.type === UiStepType.Document ? nextStep.styles : null;
  },
);

/**
 * Returns the current document in state if it the expected document for the step.
 * Else, returns undefined.
 * The doc will not match if:
 * 1. There are multiple doc steps in a single inquiry
 * 2. The doc verification failed and the doc ID was wiped from the inquiry
 * In both cases, the old doc will still be in Redux state.
 */
export const getDocumentForStep = createSelector(
  [(state: RootState) => state.document.document, getDocumentStepConfig],
  (document, config): DocumentGeneric | undefined => {
    return document?.id === config?.['document-id'] ? document : undefined;
  },
);

export const getGovernmentIdStepStyles = createSelector(
  [(inquiryState: InquiryState) => inquiryState.inquiry],
  (inquiry): StepGovernmentIdStyles | null => {
    const nextStep = inquiry.attributes['next-step'];

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return nextStep.type === UiStepType.GovernmentId ? nextStep.styles : null;
  },
);

export const getNarrowedStepConfig = <
  T extends UiStepType,
  C extends DynamicFlowStep & { type: T },
>(args: {
  state: RootState;
  stepType: T;
}): C['config'] | null => {
  const nextStep = args.state.inquiry.inquiry.attributes['next-step'];
  return nextStep.type === args.stepType && nextStep.config != null
    ? (nextStep.config as C['config'])
    : null;
};

export const getNarrowedStepStyles = <
  T extends UiStepType,
  C extends DynamicFlowStep & { type: T },
>(
  stepType: T,
) => {
  return createSelector(
    [(inquiryState: InquiryState) => inquiryState.inquiry],
    (inquiry: Inquiry): C['styles'] | null => {
      const nextStep = inquiry.attributes['next-step'];
      return nextStep.type === stepType ? (nextStep.styles as C['styles']) : null;
    },
  );
};

export const getButtonVerifyWithPersonaComponent = (
  state: RootState,
): ButtonVerifyWithPersonaComponent | null => {
  const component = state.inquiry.component;
  return component?.type === UiComponentType.ButtonVerifyWithPersona ? component : null;
};

export const getSelfieStepStyles = createSelector(
  [(inquiryState: InquiryState) => inquiryState.inquiry],
  (inquiry): StepSelfieStyles | null => {
    const nextStep = inquiry.attributes['next-step'];

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    return nextStep.type === UiStepType.Selfie ? nextStep.styles : null;
  },
);

export const getStepStyles = createSelector(
  [(state: RootState) => state.inquiry],
  (inquiryState) => {
    const nextStep = inquiryState.inquiry?.attributes['next-step'];
    switch (nextStep?.type) {
      case UiStepType.Ui:
        return nextStep.styles;
      case UiStepType.Document:
        return getDocumentStepStyles(inquiryState);
      case UiStepType.GovernmentId:
        return getGovernmentIdStepStyles(inquiryState);
      case UiStepType.Selfie:
        return getSelfieStepStyles(inquiryState);
      case UiStepType.SelfieReflection:
        return getNarrowedStepStyles<UiStepType.SelfieReflection, StepSelfieReflection>(
          UiStepType.SelfieReflection,
        )(inquiryState);
      default:
        return undefined;
    }
  },
);

export const getAttachedUploadedFiles = createSelector(
  [
    (state: RootState) => state.document.uploadedFiles,
    (state: RootState) => state.document.documentFiles,
  ],
  (uploadedFiles, documentFiles): UploadedFile[] => {
    return uploadedFiles.filter((uploadedFile) =>
      documentFiles.some((file) => file.id === uploadedFile.fileId),
    );
  },
);
